import {
    BoldItalicUnderlineToggles,
    CreateLink,
    headingsPlugin,
    linkDialogPlugin,
    listsPlugin,
    ListsToggle,
    MDXEditor,
    quotePlugin,
    thematicBreakPlugin,
    toolbarPlugin
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import api from '../api';
import { ButtonStatus } from '../common/constants';
import Button from './Button';
import StatusBadge from './StatusBadge';
import Checkbox from './Checkbox';
import Dialog from './dialogs/Dialog';
import Select from './Select';
import TextInput from './TextInput';
import styles from './EditionMailComposer.module.css';

const PRESETS = [
    { id: 'all', label: 'Tutti', matches: () => true },
    { id: 'full_access', label: 'Solo accesso completo', matches: student => student.access_mode === 'full' },
    { id: 'limited_access', label: 'Solo accesso limitato', matches: student => student.access_mode === 'limited' },
    { id: 'attended', label: 'Almeno una lezione', matches: student => student.attended },
    { id: 'not_attended', label: 'Mai frequentato', matches: student => !student.attended },
    { id: 'in_progress', label: 'In corso', matches: student => student.in_progress },
    { id: 'completed', label: 'Corso completato', matches: student => student.completed },
    { id: 'never_accessed', label: 'Mai entrati in piattaforma', matches: student => student.never_accessed },
];

const SENDER_OPTIONS = [
    { id: 0, name: 'No reply', value: 'noreply' },
    { id: 1, name: 'Education', value: 'education' },
];

const STATUS_COLORS = {
    scheduled: 'var(--primary)',
    sent: 'var(--sf-green)',
    failed: 'var(--sf-red)',
    cancelled: 'rgba(var(--text-color-rgb), 55%)',
};

const STATUS_LABELS = {
    scheduled: 'Programmata',
    sent: 'Inviata',
    failed: 'Errore',
    cancelled: 'Annullata',
    draft: 'Bozza',
};

const formatMailDate = value => value
    ? new Date(`${value}${value.endsWith('Z') || value.includes('+') ? '' : 'Z'}`).toLocaleString('it-IT')
    : '-';

const localDateTimeValue = (date) => {
    const offset = date.getTimezoneOffset();
    return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 16);
};

const EditionMailPreview = ({ markdown, subject, sender }) => (
    <div className={styles.previewShell}>
        <div className={styles.previewSubject}>{subject}</div>
        <div className={styles.previewCanvas}>
            <div className={styles.previewHeader}>
                <img
                    src="https://starting-finance-dev.fra1.cdn.digitaloceanspaces.com/emails/fonts/sfbs.png"
                    width="120"
                    height="120"
                    alt="Starting Finance Business School"
                />
            </div>
            <div className={styles.previewContent}>
                <Markdown>{markdown}</Markdown>
            </div>
            {sender === 'noreply' && (
                <div className={styles.previewNotice}>
                    Questa mail è stata inviata automaticamente, <strong>non rispondere a questo indirizzo e-mail</strong>.<br />
                    Se hai bisogno di supporto tecnico contattaci a support@startingfinance.com<br />
                    Per domande sulla didattica e sullo svolgimento dei corsi, scrivici a education@startingfinance.com
                </div>
            )}
            {sender === 'education' && (
                <div className={styles.previewSignature}>
                    <img
                        src="https://starting-finance-dev.fra1.cdn.digitaloceanspaces.com/emails/fonts/sfbs.png"
                        width="120"
                        height="120"
                        alt="Starting Finance Business School"
                    />
                    <div>
                        <strong>Starting Finance Business School</strong><br /><br />
                        <a href="https://business-school.startingfinance.com/">business-school.startingfinance.com</a><br /><br />
                        <a href="https://startingfinance.com/">startingfinance.com</a>
                    </div>
                </div>
            )}
            <div className={styles.previewFooter}>
                <div>© 2026 Starting Finance Srl. Tutti i diritti riservati.</div>
                <div>14944641001</div>
            </div>
        </div>
    </div>
);

const EditionMailComposer = ({ edition }) => {
    const editorRef = useRef(null);
    const [students, setStudents] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [activePreset, setActivePreset] = useState('all');
    const [search, setSearch] = useState('');
    const [subject, setSubject] = useState('');
    const [markdown, setMarkdown] = useState('');
    const [sender, setSender] = useState('noreply');
    const [scheduleMode, setScheduleMode] = useState('now');
    const [scheduledFor, setScheduledFor] = useState('');
    const [loadingRecipients, setLoadingRecipients] = useState(true);
    const [sendingStatus, setSendingStatus] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [loadingCampaigns, setLoadingCampaigns] = useState(true);
    const [historyPreview, setHistoryPreview] = useState(null);
    const [feedback, setFeedback] = useState(null);

    const loadCampaigns = useCallback(async () => {
        if (!edition?.id) return;
        setLoadingCampaigns(true);
        try {
            const response = await api.get(`/admin/editions/${edition.id}/mailings`, { timeout: 0 });
            setCampaigns(response?.campaigns || []);
        }
        catch (error) {
            console.error(error);
        }
        finally {
            setLoadingCampaigns(false);
        }
    }, [edition?.id]);

    useEffect(() => {
        const loadRecipients = async () => {
            setLoadingRecipients(true);
            try {
                const response = await api.get(`/admin/editions/${edition.id}/mail-recipients`, { timeout: 0 });
                const recipients = response?.students || [];
                setStudents(recipients);
                setSelectedIds(new Set(recipients.map(student => student.id)));
            }
            catch (error) {
                console.error(error);
                setFeedback({ type: 'error', text: error?.detail || 'Impossibile caricare gli studenti.' });
            }
            finally {
                setLoadingRecipients(false);
            }
        };

        if (edition?.id) {
            loadRecipients();
        }
    }, [edition?.id]);

    useEffect(() => {
        loadCampaigns();
    }, [loadCampaigns]);

    const presetCounts = useMemo(() => Object.fromEntries(
        PRESETS.map(preset => [preset.id, students.filter(preset.matches).length])
    ), [students]);

    const visibleStudents = useMemo(() => {
        const normalized = search.trim().toLowerCase();
        if (!normalized) return students;
        return students.filter(student => (
            `${student.name || ''} ${student.surname || ''} ${student.email || ''}`
                .toLowerCase()
                .includes(normalized)
        ));
    }, [search, students]);

    const selectedStudents = useMemo(
        () => students.filter(student => selectedIds.has(student.id)),
        [selectedIds, students]
    );

    const applyPreset = (preset) => {
        setSelectedIds(new Set(students.filter(preset.matches).map(student => student.id)));
        setActivePreset(preset.id);
    };

    const toggleStudent = (studentId, selected) => {
        setSelectedIds(previous => {
            const next = new Set(previous);
            if (selected) next.add(studentId);
            else next.delete(studentId);
            return next;
        });
        setActivePreset(null);
    };

    const openPreview = () => {
        if (!subject.trim() || !markdown.trim() || selectedStudents.length === 0) return;
        if (scheduleMode === 'later' && !scheduledFor) return;
        setFeedback(null);
        setPreviewOpen(true);
    };

    const createMailings = async () => {
        setSendingStatus(ButtonStatus.Loading);
        try {
            const payload = {
                subject: subject.trim(),
                markdown: markdown.trim(),
                user_ids: selectedStudents.map(student => student.id),
                scheduled_for: scheduleMode === 'later' ? new Date(scheduledFor).toISOString() : null,
                sender,
            };
            const response = await api.post(`/admin/editions/${edition.id}/mailings`, payload);
            setPreviewOpen(false);
            setSendingStatus(ButtonStatus.Success);
            setFeedback({
                type: 'success',
                text: `${response.created} ${response.created === 1 ? 'mail programmata' : 'mail programmate'} correttamente.`,
            });
            setSubject('');
            setMarkdown('');
            editorRef.current?.setMarkdown('');
            setScheduleMode('now');
            setScheduledFor('');
            setSender('noreply');
            await loadCampaigns();
        }
        catch (error) {
            console.error(error);
            setSendingStatus(ButtonStatus.Error);
            setFeedback({ type: 'error', text: error?.detail || 'Impossibile programmare le mail.' });
        }
        finally {
            window.setTimeout(() => setSendingStatus(null), 1500);
        }
    };

    const previewDisabled = (
        loadingRecipients
        || selectedStudents.length === 0
        || !subject.trim()
        || !markdown.trim()
        || (scheduleMode === 'later' && !scheduledFor)
    );

    return (
        <div className={styles.sectionStack}>
            <div className={styles.mailClient}>
                <aside className={styles.recipientsPanel}>
                <div className={styles.panelHeader}>
                    <div>
                        <div className={styles.eyebrow}>DESTINATARI</div>
                        <div className={styles.recipientCount}>{selectedStudents.length} di {students.length} selezionati</div>
                    </div>
                    <button
                        type="button"
                        className={styles.textAction}
                        onClick={() => {
                            setSelectedIds(new Set());
                            setActivePreset(null);
                        }}
                    >
                        Deseleziona tutti
                    </button>
                </div>

                <div className={styles.presets}>
                    {PRESETS.map(preset => (
                        <button
                            type="button"
                            key={preset.id}
                            className={`${styles.preset} ${activePreset === preset.id ? styles.presetActive : ''}`}
                            onClick={() => applyPreset(preset)}
                        >
                            <span>{preset.label}</span>
                            <span className={styles.presetCount}>{presetCounts[preset.id] || 0}</span>
                        </button>
                    ))}
                </div>

                <TextInput appearance="modern"
                    type="search"
                    value={search}
                    placeholder="Cerca studente..."
                    onKeyUp={setSearch}
                    style={{ backgroundColor: 'var(--background-secondary-color)' }}
                />

                <div className={styles.recipientList}>
                    {loadingRecipients && <div className={styles.emptyState}>Caricamento studenti...</div>}
                    {!loadingRecipients && visibleStudents.length === 0 && <div className={styles.emptyState}>Nessuno studente trovato.</div>}
                    {visibleStudents.map(student => (
                        <div key={student.id} className={`${styles.recipient} ${selectedIds.has(student.id) ? '' : styles.recipientExcluded}`}>
                            <Checkbox
                                value={selectedIds.has(student.id)}
                                onChange={selected => toggleStudent(student.id, selected)}
                                size="1.25rem"
                                radius="5px"
                            />
                            <div className={styles.avatar}>{(student.name || student.email || '?').charAt(0).toUpperCase()}</div>
                            <div className={styles.recipientIdentity}>
                                <div className={styles.recipientName}>{[student.name, student.surname].filter(Boolean).join(' ') || 'Studente'}</div>
                                <div className={styles.recipientEmail}>{student.email}</div>
                            </div>
                            {!selectedIds.has(student.id) && <span className={styles.excludedLabel}>Escluso</span>}
                        </div>
                    ))}
                </div>
                </aside>

                <div className={styles.composerPanel}>
                <div className={styles.composeHeader}>
                    <div>
                        <div className={styles.eyebrow}>NUOVO MESSAGGIO</div>
                        <div className={styles.composeTitle}>{edition.name}</div>
                    </div>
                    <div className={styles.toSummary}>A: {selectedStudents.length} studenti</div>
                </div>

                {feedback && <div className={`${styles.feedback} ${feedback.type === 'error' ? styles.feedbackError : styles.feedbackSuccess}`}>{feedback.text}</div>}

                <div className={styles.senderRow}>
                    <span>Da</span>
                    <Select appearance="modern"
                        selected={SENDER_OPTIONS.find(option => option.value === sender)}
                        options={SENDER_OPTIONS}
                        onSelect={option => setSender(option.value)}
                        style={{ width: '100%' }}
                    />
                </div>

                <div className={styles.subjectRow}>
                    <label htmlFor={`edition-mail-subject-${edition.id}`}>Oggetto</label>
                    <input
                        id={`edition-mail-subject-${edition.id}`}
                        value={subject}
                        maxLength={250}
                        placeholder="Scrivi l'oggetto della mail"
                        onChange={event => setSubject(event.target.value)}
                    />
                </div>

                <div className={styles.editorWrap}>
                    <MDXEditor
                        ref={editorRef}
                        contentEditableClassName={styles.markdownEditor}
                        markdown={markdown}
                        placeholder="Scrivi qui il testo della mail"
                        onChange={setMarkdown}
                        plugins={[
                            headingsPlugin(),
                            listsPlugin(),
                            quotePlugin(),
                            thematicBreakPlugin(),
                            linkDialogPlugin(),
                            toolbarPlugin({
                                toolbarContents: () => (
                                    <>
                                        <BoldItalicUnderlineToggles options={["Bold", "Italic"]} />
                                        <ListsToggle />
                                        <CreateLink />
                                    </>
                                )
                            })
                        ]}
                    />
                </div>

                <div className={styles.scheduleBar}>
                    <div className={styles.scheduleOptions}>
                        <label className={styles.radioOption}>
                            <input type="radio" checked={scheduleMode === 'now'} onChange={() => setScheduleMode('now')} />
                            <span>Invia appena possibile</span>
                        </label>
                        <label className={styles.radioOption}>
                            <input
                                type="radio"
                                checked={scheduleMode === 'later'}
                                onChange={() => {
                                    setScheduleMode('later');
                                    if (!scheduledFor) setScheduledFor(localDateTimeValue(new Date(Date.now() + 3600000)));
                                }}
                            />
                            <span>Programma invio</span>
                        </label>
                        {scheduleMode === 'later' && (
                            <input
                                className={styles.dateTimeInput}
                                type="datetime-local"
                                min={localDateTimeValue(new Date())}
                                value={scheduledFor}
                                onChange={event => setScheduledFor(event.target.value)}
                            />
                        )}
                    </div>
                    <Button requiresEdit disabled={previewDisabled} appearance="subtle" onClick={openPreview}>
                        Anteprima e conferma
                    </Button>
                </div>
                </div>
            </div>

            <div className={styles.historyPanel}>
                <div className={styles.historyHeader}>
                    <div>
                        <div className={styles.eyebrow}>STORICO</div>
                        <div className={styles.historyTitle}>Mail dell'edizione</div>
                    </div>
                    <div className={styles.historyCount}>{campaigns.length} {campaigns.length === 1 ? 'invio' : 'invii'}</div>
                </div>

                {loadingCampaigns && <div className={styles.emptyState}>Caricamento storico...</div>}
                {!loadingCampaigns && campaigns.length === 0 && (
                    <div className={styles.emptyHistory}>Non sono ancora state programmate mail personalizzate per questa edizione.</div>
                )}
                {!loadingCampaigns && campaigns.length > 0 && (
                    <div className={styles.campaignList}>
                        {campaigns.map(campaign => (
                            <div className={styles.campaignCard} key={campaign.id}>
                                <div className={styles.campaignMain}>
                                    <div className={styles.campaignIdentity}>
                                        <div className={styles.campaignSubject}>{campaign.subject}</div>
                                        <div className={styles.campaignMeta}>
                                            <span>{campaign.sender === 'education' ? 'Education' : 'No reply'}</span>
                                            <span>•</span>
                                            <span>{campaign.recipient_count} {campaign.recipient_count === 1 ? 'destinatario' : 'destinatari'}</span>
                                            <span>•</span>
                                            <span>Programmato: {formatMailDate(campaign.scheduled_for)}</span>
                                        </div>
                                    </div>
                                    <div className={styles.campaignActions}>
                                        <div className={styles.statusSummary}>
                                            {Object.entries(campaign.statuses || {}).map(([status, count]) => (
                                                <div key={status} className={styles.softStatus}>
                                                    <StatusBadge appearance="soft" label={`${count} ${STATUS_LABELS[status] || status}`} color={STATUS_COLORS[status] || 'rgba(var(--text-color-rgb), 55%)'} />
                                                </div>
                                            ))}
                                        </div>
                                        <Button appearance="subtle" onClick={() => setHistoryPreview(campaign)}>Anteprima</Button>
                                    </div>
                                </div>

                                <details className={styles.recipientsDetails}>
                                    <summary>Vedi destinatari</summary>
                                    <div className={styles.historyRecipients}>
                                        {campaign.recipients.map(recipient => (
                                            <div className={styles.historyRecipient} key={recipient.mailing_id}>
                                                <div className={styles.historyRecipientIdentity}>
                                                    <strong>{[recipient.name, recipient.surname].filter(Boolean).join(' ') || 'Studente'}</strong>
                                                    <span>{recipient.email}</span>
                                                    {recipient.last_error && <span className={styles.recipientError}>{recipient.last_error}</span>}
                                                </div>
                                                <div className={styles.softStatus}>
                                                    <StatusBadge appearance="soft" label={STATUS_LABELS[recipient.status] || recipient.status} color={STATUS_COLORS[recipient.status] || 'rgba(var(--text-color-rgb), 55%)'} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </details>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Dialog
                title="Anteprima e conferma invio"
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                style={{ width: '92vw', maxWidth: '1000px' }}
            >
                <div className={styles.confirmDialog}>
                    <div className={styles.confirmSummary}>
                        <div><strong>Destinatari:</strong> {selectedStudents.length} studenti</div>
                        <div><strong>Mittente:</strong> {sender === 'education' ? 'Education' : 'No reply'}</div>
                        <div><strong>Invio:</strong> {scheduleMode === 'now' ? 'appena possibile' : new Date(scheduledFor).toLocaleString('it-IT')}</div>
                    </div>
                    <EditionMailPreview subject={subject} markdown={markdown} sender={sender} />
                    <div className={styles.confirmActions}>
                        <Button appearance="subtle" accentColor="var(--sf-red)" onClick={() => setPreviewOpen(false)}>Annulla</Button>
                        <Button requiresEdit status={sendingStatus} disabled={sendingStatus === ButtonStatus.Loading} onClick={createMailings}>
                            Conferma e programma
                        </Button>
                    </div>
                </div>
            </Dialog>

            <Dialog
                title={historyPreview?.subject ?? 'Anteprima mail'}
                open={historyPreview}
                onClose={() => setHistoryPreview(null)}
                style={{ width: '80vw', maxWidth: '900px' }}
                contentMaxHeight="70vh"
                overflow="hidden"
            >
                <iframe
                    title={historyPreview?.subject ?? 'Anteprima mail'}
                    srcDoc={historyPreview?.content ?? ''}
                    sandbox=""
                    style={{ width: '100%', height: '65vh', border: 0, background: '#f4f4f4' }}
                />
            </Dialog>
        </div>
    );
};

export default EditionMailComposer;
